/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  BreakpointQueryName,
  Button, ButtonColor, ButtonProps, ICON_CHECK, ICON_EMPTY, Link, Symbol, SymbolDirection,
} from '@doveit/lego';
import React from 'react';
import { useTheme } from 'styled-components';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';
import * as styles from './PackageCard.style';

export interface PackageCardProps {
  title: string,
  description?: string,
  price?: string,
  priceFrequency?: string,
  discountedPrice?: string,
  pricePlaceholder?: string,
  features?: string[],
  highlighted?: boolean,
  badge?: string,
  notes?: string[],
  cta?: Pick<ButtonProps, 'label' | 'onClick' | 'href' | 'dataRef' | 'dataTracking'>,
  limitShownAdvantagesForMobile?: number,
}

const PackageCard: React.FC<PackageCardProps> = ({
  title,
  description,
  price,
  priceFrequency,
  discountedPrice,
  pricePlaceholder,
  features = [],
  notes,
  highlighted,
  badge,
  cta,
  limitShownAdvantagesForMobile = 5,
}) => {
  const [allFeaturesShown, setAllFeaturesShown] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BreakpointQueryName.MD));

  const truncatedFeatures = React.useMemo(
    () => ((isMobile && !allFeaturesShown) ? features.slice(0, limitShownAdvantagesForMobile) : features),
    [features, isMobile, limitShownAdvantagesForMobile, allFeaturesShown],
  );

  return (
    <div css={styles.base}>
      {badge && (
        <div
          aria-label="badge"
          css={styles.badge}
        >
          {badge}
        </div>
      )}
      <div
        css={styles.title}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {price && (
        <div css={styles.priceBox}>
          <span
            css={styles.price}
            $discountedPrice={discountedPrice}
          >
            { priceFrequency ? `€ ${price}` : price }
          </span>
          {discountedPrice && (
            <span
              css={styles.discountedPrice}
              aria-label="Prezzo del pacchetto scontato"
            >
              {discountedPrice}
            </span>
          )}
          {!priceFrequency && (
            <small>euro</small>
          )}
          {priceFrequency && (
            <small>
              {`/ ${priceFrequency}`}
            </small>
          )}
        </div>
      )}
      {!price && pricePlaceholder && (
        <div css={styles.pricePlaceholder}>
          {pricePlaceholder}
        </div>
      )}
      {description && (
        <div css={styles.description}>
          {description}
        </div>
      )}
      <div css={styles.body}>
        {features?.length > 0 && (
          <ul css={styles.features}>
            {truncatedFeatures.map((feature) => (
              <li
                key={`${title}-${feature}`}
                css={styles.feature}
                $highlighted={highlighted}
                aria-label="Vantaggio compreso nel pacchetto"
              >
                <Symbol path={ICON_CHECK} /> <span dangerouslySetInnerHTML={{ __html: feature }} />
              </li>
            ))}
          </ul>
        )}
        {(notes && (allFeaturesShown || !isMobile || limitShownAdvantagesForMobile >= features.length)) && (
          <div
            css={styles.notes}
            aria-label="Note del pacchetto"
          >
            {notes.map((note) => (
              <div key={note}>
                {note}
              </div>
            ))}
          </div>
        )}
        {isMobile && !allFeaturesShown && truncatedFeatures.length < features.length && (
          <Link
            css={styles.showMore}
            aria-label="Pulsante per mostrare tutti i vantaggi del pacchetto"
            onClick={() => { setAllFeaturesShown(true); }}
          >
            <Symbol path={ICON_EMPTY} direction={SymbolDirection.BOTTOM} />
            Mostra tutti i vantaggi
          </Link>
        )}
        {cta && (
          <div css={styles.action}>
            <Button
              {...cta}
              aria-label="Azione del pacchetto"
              expanded
              color={highlighted ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PackageCard;
